import { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  clearThumbnail,
  reset,
  setActionInduction,
  setBackgroundColor,
  setDominantColors,
  setFontColor,
  setFontColorLegal,
  setLandingUrl,
  setProposalPhraseBottom,
  setProposalPhraseTop,
  setThumbnail,
} from './thumbnailCustomSlice'
import PropTypes from 'prop-types'
import { Assets, FontColorNames, TalkChannelFontColors } from '../../../Codes'
import Preview from '../../preview/Preview'
import { ImageSizes } from '../../../image/ImageSizes'
import ImageLibraryUpload from '../common/ImageLibraryUpload'
import ColorPicker from '../common/ColorPicker'
import TextInput from '../../../../components/form/TextInput'
import RadioButton from '../../../../components/form/RadioButton'
import { checkLegalNotice, getImageColors } from '../../creativeUtil'
import ErrorText from '../../../../components/form/ErrorText'
import { isNullOrUndefined } from '../../../../utils'
import { useParams } from 'react-router-dom'
import HelpTooltip from '../../../../components/tooltip/HelpTooltip'
import { GitbookLinks } from '../../../../utils/constants'

const ThumbnailCustom = ({ disabled, errors }) => {
  const dispatch = useDispatch()
  const {
    creativeAdd: { importedId },
    buildCreative: { channel },
    assets: {
      thumbnailCustom: {
        dominantColors,
        thumbnail,
        backgroundColor,
        proposalPhraseTop,
        proposalPhraseBottom,
        actionInduction,
        fontColor,
        fontColorLegal,
        landingUrl,
      },
    },
  } = useSelector(state => state.creative, shallowEqual)
  const { creativeId } = useParams()
  const isLegalNotice = useMemo(() => checkLegalNotice(channel), [channel])
  const imported =
    !isNullOrUndefined(creativeId) || !isNullOrUndefined(importedId)

  useEffect(() => () => dispatch(reset()), [dispatch])
  useEffect(() => {
    if (!disabled && !imported && channel) {
      dispatch(
        setLandingUrl(
          `${channel.siteUrl.mobileUrl.protocol.toLowerCase()}://${
            channel.siteUrl.mobileUrl.url
          }`
        )
      )
    }
  }, [disabled, imported, channel, dispatch])

  const handleImageUpload = useCallback(
    async selectedImages => {
      const [image] = selectedImages
      dispatch(setThumbnail(image))
      const colors = await getImageColors(image.url)
      dispatch(setDominantColors(colors))
    },
    [dispatch]
  )
  const handleImageDelete = useCallback(() => {
    dispatch(clearThumbnail())
  }, [dispatch])
  const handleBackgroundColorChange = useCallback(
    value => dispatch(setBackgroundColor(value)),
    [dispatch]
  )
  const handleProposalPhraseTopChange = useCallback(
    value => dispatch(setProposalPhraseTop(value)),
    [dispatch]
  )
  const handleProposalPhraseBottomChange = useCallback(
    value => dispatch(setProposalPhraseBottom(value)),
    [dispatch]
  )
  const handleActionInductionChange = useCallback(
    value => dispatch(setActionInduction(value)),
    [dispatch]
  )
  const handleLandingUrlChange = useCallback(
    value => dispatch(setLandingUrl(value)),
    [dispatch]
  )

  return (
    <>
      <div className="add_material">
        <div className="material_item">
          <div className="tit_itemwrap">
            <strong className="tit_item off in_active">맞춤소재</strong>
          </div>
          <div className="desc_item">
            <span className="txt_item">
              구매 키워드 매칭 시 노출되는 소재입니다.
            </span>
          </div>
        </div>
      </div>
      <div className="cont_material">
        <div className="group_material">
          <div className="box_material">
            <div className="inner_material">
              <strong className="screen_out">기본설정</strong>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">채널명</strong>
                </div>
                <div className="desc_item">
                  {channel?.talkChannel?.name || '비즈채널을 선택하세요.'}
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">썸네일 이미지</strong>
                </div>
                <div className="desc_item">
                  <ImageLibraryUpload
                    handleImageUpload={handleImageUpload}
                    onClickDelete={handleImageDelete}
                    maxCount={1}
                    requiredImageSize={[ImageSizes['1200x380']]}
                    imageUrl={thumbnail?.url}
                    imageName={thumbnail?.name}
                    imageSize={thumbnail?.size}
                    disabled={disabled}
                    guideLink={GitbookLinks.TALK_CHANNEL_REGISTRATION}
                  />
                  <ErrorText error={errors?.thumbnail} />
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">배경 색상</strong>
                </div>
                <div className="desc_item">
                  <ColorPicker
                    disabled={disabled}
                    value={backgroundColor}
                    colors={dominantColors}
                    onChange={handleBackgroundColorChange}
                    error={errors?.backgroundColor}
                  />
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">홍보문구</strong>
                </div>
                <div className="desc_item">
                  <div className="multi_row">
                    <div className="subtit_itemwrap">
                      <em className="subtit_item">상단 홍보문구</em>
                    </div>
                    <TextInput
                      disabled={disabled}
                      value={proposalPhraseTop}
                      onValueChange={handleProposalPhraseTopChange}
                      maxLength={20}
                      placeholder="상단 홍보문구를 입력하세요."
                      trim
                      error={errors?.proposalPhraseTop}
                    />
                    <ErrorText error={errors?.proposalPhraseTop} />
                  </div>
                  <div className="multi_row">
                    <div className="subtit_itemwrap">
                      <em className="subtit_item">하단 홍보문구</em>
                    </div>
                    <TextInput
                      disabled={disabled}
                      value={proposalPhraseBottom}
                      onValueChange={handleProposalPhraseBottomChange}
                      maxLength={18}
                      placeholder="하단 홍보문구를 입력하세요."
                      trim
                      error={errors?.proposalPhraseBottom}
                    />
                    <ErrorText error={errors?.proposalPhraseBottom} />
                  </div>
                  <div className="multi_row">
                    <div className="subtit_itemwrap">
                      <em className="subtit_item">행동유도버튼</em>
                    </div>
                    <TextInput
                      disabled={disabled}
                      value={actionInduction}
                      onValueChange={handleActionInductionChange}
                      maxLength={6}
                      placeholder="문구를 입력하세요."
                      trim
                      error={errors?.actionInduction}
                    />
                    <ErrorText error={errors?.actionInduction} />
                  </div>
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">
                    문구 색상
                    <HelpTooltip>
                      문구 색상은 배경 색상 지정 후 선택 가능합니다.
                    </HelpTooltip>
                  </strong>
                </div>
                <div className="desc_item">
                  <div className="multi_row">
                    <div className="subtit_itemwrap">
                      <em className="subtit_item">홍보문구</em>
                    </div>
                    <div className="item_filter">
                      <RadioButton.Group className="filter_radio">
                        <RadioButton
                          disabled={disabled || backgroundColor === undefined}
                          checkValue={fontColor}
                          value={TalkChannelFontColors.BLACK}
                          label={FontColorNames[TalkChannelFontColors.BLACK]}
                          onValueChange={() =>
                            dispatch(setFontColor(TalkChannelFontColors.BLACK))
                          }
                        />
                        <RadioButton
                          disabled={disabled || backgroundColor === undefined}
                          checkValue={fontColor}
                          value={TalkChannelFontColors.WHITE}
                          label={FontColorNames[TalkChannelFontColors.WHITE]}
                          onValueChange={() =>
                            dispatch(setFontColor(TalkChannelFontColors.WHITE))
                          }
                        />
                      </RadioButton.Group>
                    </div>
                  </div>
                  {isLegalNotice && (
                    <div className="multi_row">
                      <div className="subtit_itemwrap">
                        <em className="subtit_item">법적고지문구</em>
                        <HelpTooltip>
                          법적고지문구 입력 시 해당 문구 색상이 적용됩니다.
                        </HelpTooltip>
                      </div>
                      <div className="item_filter">
                        <RadioButton.Group className="filter_radio">
                          <RadioButton
                            disabled={disabled || backgroundColor === undefined}
                            checkValue={fontColorLegal}
                            value={TalkChannelFontColors.BLACK}
                            label={FontColorNames[TalkChannelFontColors.BLACK]}
                            onValueChange={() =>
                              dispatch(
                                setFontColorLegal(TalkChannelFontColors.BLACK)
                              )
                            }
                          />
                          <RadioButton
                            disabled={disabled || backgroundColor === undefined}
                            checkValue={fontColorLegal}
                            value={TalkChannelFontColors.WHITE}
                            label={FontColorNames[TalkChannelFontColors.WHITE]}
                            onValueChange={() =>
                              dispatch(
                                setFontColorLegal(TalkChannelFontColors.WHITE)
                              )
                            }
                          />
                        </RadioButton.Group>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="material_item">
                <div className="tit_itemwrap">
                  <strong className="tit_item">랜딩URL</strong>
                </div>
                <div className="desc_item">
                  <div className="multi_row">
                    <div className="subtit_itemwrap">
                      <em className="subtit_item">모바일</em>
                    </div>
                    <TextInput
                      disabled={disabled}
                      value={landingUrl}
                      onValueChange={handleLandingUrlChange}
                      placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                      trim
                      hideLength
                      error={errors?.landingUrl}
                    />
                    <ErrorText error={errors?.landingUrl} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Preview
            type={Assets.THUMBNAIL_CUSTOM.name}
            data={{
              dominantColors,
              thumbnail,
              backgroundColor,
              proposalPhraseTop,
              proposalPhraseBottom,
              actionInduction,
              fontColor,
              fontColorLegal,
              landingUrl,
            }}
          />
        </div>
      </div>
    </>
  )
}
ThumbnailCustom.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    thumbnail: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    backgroundColor: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    proposalPhraseTop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    proposalPhraseBottom: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
    actionInduction: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    landingUrl: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
}
export default ThumbnailCustom
